import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/ledgerApi";
import { Loader2, Plus, Minus, ChefHat, ArrowLeft, RotateCcw, Sparkles, CheckCircle2, Layers, ListOrdered, MessageCircle, Download } from "lucide-react";
import { A_PATH } from "@/components/HeroLogo";

export const inr = (n) =>
  `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 1 })}`;

const STATUS_BADGE = {
  off_menu: { label: "off menu", cls: "text-[rgba(240,233,216,0.45)] border-[rgba(240,233,216,0.2)]" },
};

export const COST_COMPONENTS = [
  { label: "50% Standard Profit", slug: "standard-profit", pct: 0.5 },
  { label: "Packaging 15%", slug: "packaging", pct: 0.15 },
  { label: "Labour 10%", slug: "labour", pct: 0.1 },
  { label: "Electricity 5%", slug: "electricity", pct: 0.05 },
  { label: "Wastage 5%", slug: "wastage", pct: 0.05 },
];

const SURPLUS_PCT = {
  "tea-cakes-banana-streusel": 60,
  "brownies-chilli-orange": 40,
  "signature-bundts-marble-1-kg": 70,
  "brownies-raspberry-chocolate": 20,
  "savories-rustic-herb-loaf": 250,
  "savories-chilli-garlic-crunch": 205,
};

const TWO_PC_SURPLUS = 15;
const MINI_BYTES_SURPLUS = 50;

const SURPLUS_BY_ID = {
  "savories-seeded-pesto-twist-halfkg": 25,
  "savories-chilli-garlic-crunch-halfkg": 240,
};

export function surplusFor(pid) {
  if (SURPLUS_BY_ID[pid] !== undefined) return SURPLUS_BY_ID[pid];
  const isMiniBytes = pid.endsWith("-minibytes");
  const is2pc = pid.endsWith("-2pc");
  const isMiniLoaf = pid.endsWith("-miniloaf");
  const isHalfKg = pid.endsWith("-halfkg");
  const isMini = !isMiniLoaf && !isMiniBytes && pid.endsWith("-mini");
  const base = isMiniBytes
    ? pid.slice(0, -10)
    : is2pc
    ? pid.slice(0, -4)
    : isMiniLoaf
    ? pid.slice(0, -9)
    : isHalfKg
    ? pid.slice(0, -7)
    : isMini
    ? pid.slice(0, -5)
    : pid;
  const surcharge = (is2pc && !base.startsWith("centrefilled-muffins")) || isMini;
  return (SURPLUS_PCT[base] || 0) + (surcharge ? TWO_PC_SURPLUS : 0) + (isMiniBytes ? MINI_BYTES_SURPLUS : 0);
}

export const FLAT_PRICE = { "Dip Pots": 100 };

export function computePricing(totalCost, items, extraPct) {
  const base = 1 + COST_COMPONENTS.reduce((a, c) => a + c.pct, 0);
  const flatItems = (items || []).filter((it) => FLAT_PRICE[it.category]);
  const flats = flatItems.map((it) => ({
    id: it.product_id,
    name: it.name,
    category: it.category,
    qty: it.qty,
    unit: FLAT_PRICE[it.category],
    amount: FLAT_PRICE[it.category] * it.qty,
  }));
  const flatCost = flatItems.reduce((a, it) => a + it.cost, 0);
  const ladderCost = totalCost - flatCost;
  const subtotal = ladderCost * base;
  const pct = Math.max(0, parseFloat(extraPct) || 0);
  const withExtra = subtotal * (1 + pct / 100);
  const surpluses = (items || [])
    .filter((it) => !FLAT_PRICE[it.category] && surplusFor(it.product_id) > 0)
    .map((it) => ({
      id: it.product_id,
      name: it.name,
      pct: surplusFor(it.product_id),
      amount: it.cost * base * (surplusFor(it.product_id) / 100),
    }));
  const flatTotal = flats.reduce((a, f) => a + f.amount, 0);
  const total = withExtra + surpluses.reduce((a, s) => a + s.amount, 0) + flatTotal;
  const mrp = Math.round(total / 10) * 10;
  return { ladderCost, flatCost, subtotal, pct, withExtra, surpluses, flats, flatTotal, total, mrp };
}

function ProductRow({ product, qty, onChange }) {
  const badge = STATUS_BADGE[product.status];
  const [text, setText] = useState(String(qty));
  useEffect(() => {
    setText(qty > 0 ? String(qty) : "");
  }, [qty]);

  const handleTyped = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 3);
    setText(digits);
    if (digits !== "") onChange(product.id, Math.min(parseInt(digits, 10), 200));
  };

  const handleBlur = () => {
    if (text === "" || parseInt(text || "0", 10) === 0) onChange(product.id, 0);
    else setText(String(qty));
  };

  return (
    <div
      data-testid={`bake-product-row-${product.id}`}
      className={`flex items-center gap-3 py-3 border-b border-[rgba(179,145,74,0.15)] transition-colors ${
        qty > 0 ? "bg-[rgba(179,145,74,0.07)] -mx-3 px-3 rounded-[10px] border-transparent" : ""
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-body text-[var(--hoa-cream-50)] text-sm sm:text-base leading-snug">{product.name}</p>
        {badge && (
          <span className={`inline-block mt-1 font-label text-[7.5px] tracking-[0.2em] uppercase border rounded-full px-2 py-0.5 ${badge.cls}`}>
            {badge.label}
          </span>
        )}
      </div>
      {qty > 0 && (
        <button
          type="button"
          data-testid={`bake-minus-${product.id}`}
          onClick={() => onChange(product.id, qty - 1)}
          aria-label={`Remove one ${product.name}`}
          className="shrink-0 w-10 h-10 rounded-full border border-[rgba(179,145,74,0.45)] flex items-center justify-center text-[var(--hoa-cream-50)] active:scale-95 transition-transform"
        >
          <Minus className="w-4 h-4" strokeWidth={2} />
        </button>
      )}
      {qty > 0 && (
        <input
          data-testid={`bake-qty-${product.id}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={text}
          onChange={handleTyped}
          onBlur={handleBlur}
          aria-label={`Quantity of ${product.name}`}
          className="shrink-0 w-12 h-10 text-center rounded-[10px] border border-[rgba(179,145,74,0.45)] bg-[rgba(240,233,216,0.06)] font-display text-[var(--hoa-gold-300)] text-lg focus:outline-none focus:border-[var(--hoa-gold-400)]"
        />
      )}
      <button
        type="button"
        data-testid={`bake-plus-${product.id}`}
        onClick={() => onChange(product.id, qty + 1)}
        aria-label={`Add one ${product.name}`}
        className="shrink-0 w-10 h-10 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] flex items-center justify-center shadow active:scale-95 transition-transform"
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
      </button>
    </div>
  );
}

function PricingLadder({ totalCost, items, extraPct, setExtraPct }) {
  const { ladderCost, subtotal, withExtra, surpluses, flats, total, mrp } = computePricing(totalCost, items, extraPct);

  const rowCls = "flex items-center justify-between gap-3 py-2";
  const labelCls2 = "font-body text-[rgba(11,12,13,0.6)] text-sm";
  const valCls = "font-body text-[var(--hoa-ink-950)] text-sm shrink-0";

  return (
    <div className="mt-5 border-t border-[rgba(11,12,13,0.08)] pt-4 text-left" data-testid="bake-pricing-ladder">
      <p className="font-label text-[9px] tracking-[0.26em] uppercase text-[#8A6D33]">Pricing</p>
      <div className={rowCls}>
        <p className={labelCls2}>Making cost</p>
        <p className={valCls}>{inr(ladderCost)}</p>
      </div>
      {COST_COMPONENTS.map((c) => (
        <div key={c.slug} className={rowCls}>
          <p className={labelCls2}>{c.label}</p>
          <p className={valCls} data-testid={`bake-breakdown-${c.slug}`}>+ {inr(ladderCost * c.pct)}</p>
        </div>
      ))}
      <div className={`${rowCls} border-t border-[rgba(11,12,13,0.08)]`}>
        <p className="font-body text-[var(--hoa-ink-950)] text-sm">Subtotal</p>
        <p className={`${valCls} font-medium`} data-testid="bake-subtotal">{inr(subtotal)}</p>
      </div>
      <div className={rowCls}>
        <div className="flex items-center gap-2 min-w-0">
          <p className={labelCls2}>+ Your profit</p>
          <input
            data-testid="bake-extra-pct-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="0"
            value={extraPct}
            onChange={(e) => setExtraPct(e.target.value.replace(/[^\d.]/g, "").slice(0, 5))}
            className="w-16 h-9 px-2 text-center rounded-[8px] border border-[rgba(179,145,74,0.5)] bg-white font-body text-sm text-[var(--hoa-ink-950)] focus:outline-none focus:border-[#8A6D33]"
          />
          <p className={labelCls2}>%</p>
        </div>
        <p className={valCls} data-testid="bake-extra-price">{inr(withExtra)}</p>
      </div>
      {surpluses.map((s) => (
        <div key={s.id} className={rowCls}>
          <p className={labelCls2}>{s.name} surplus {s.pct}%</p>
          <p className={valCls} data-testid={`bake-surplus-${s.id}`}>+ {inr(s.amount)}</p>
        </div>
      ))}
      {flats.map((f) => (
        <div key={f.id} className={rowCls}>
          <p className={labelCls2}>
            {f.category} · {f.name} · {inr(f.unit)} × {f.qty}
          </p>
          <p className={valCls} data-testid={`bake-flat-${f.id}`}>+ {inr(f.amount)}</p>
        </div>
      ))}
      {(surpluses.length > 0 || flats.length > 0) && (
        <div className={rowCls}>
          <p className="font-body text-[var(--hoa-ink-950)] text-sm">
            {surpluses.length > 0 ? "Total with surplus" : "Grand total"}
          </p>
          <p className={`${valCls} font-medium`} data-testid="bake-total-with-surplus">{inr(total)}</p>
        </div>
      )}
      <div className={rowCls}>
        <p className={labelCls2}>Rounded to nearest ₹10</p>
        <p className={valCls} data-testid="bake-rounded-price">{inr(mrp)}</p>
      </div>
      <div className="mt-3 rounded-[var(--hoa-radius-md)] bg-[var(--hoa-emerald-950)] border border-[rgba(179,145,74,0.45)] px-4 py-4 flex items-center justify-between gap-3">
        <p className="font-label text-[9px] tracking-[0.24em] uppercase text-[rgba(240,233,216,0.7)]">
          MRP for the client
        </p>
        <p data-testid="bake-mrp" className="font-display hoa-gold-text text-3xl sm:text-4xl">{inr(mrp)}</p>
      </div>
    </div>
  );
}

function SummaryView({ result, onConfirm, onEdit, onNew, extraPct, setExtraPct }) {
  const flatItems = result.items.filter((it) => FLAT_PRICE[it.category]);
  const ladderItems = result.items.filter((it) => !FLAT_PRICE[it.category]);
  const ladderCost = result.total_cost - flatItems.reduce((a, it) => a + it.cost, 0);
  const ladderUnits = ladderItems.reduce((a, it) => a + it.qty, 0);
  const flatUnits = flatItems.reduce((a, it) => a + it.qty, 0);
  const countParts = [];
  if (ladderUnits > 0) countParts.push(`${ladderUnits} ${ladderUnits === 1 ? "bake" : "bakes"}`);
  if (flatUnits > 0) countParts.push(`${flatUnits} ${flatUnits === 1 ? "dip pot" : "dip pots"}`);
  return (
    <div data-testid="bake-summary" className="mt-8">
      <div className="relative bg-[var(--hoa-cream-50)] rounded-[var(--hoa-radius-lg)] shadow-[var(--hoa-shadow-card)] p-6 sm:p-8 text-center">
        <div className="absolute inset-3 rounded-[calc(var(--hoa-radius-lg)-10px)] border border-[rgba(179,145,74,0.35)] pointer-events-none" />
        <p className="font-label text-[9px] tracking-[0.26em] uppercase text-[#8A6D33]">Making Cost</p>
        <p data-testid="bake-total-cost" className="font-display hoa-gold-text-deep text-5xl sm:text-6xl mt-2">
          {inr(ladderCost)}
        </p>
        <p className="font-body text-[rgba(11,12,13,0.6)] text-sm mt-3">
          {countParts.join(" + ")}
          {result.customer_name ? ` · for ${result.customer_name}` : ""}
          {result.customer_phone ? ` (${result.customer_phone})` : ""}
        </p>
        <div className="mt-5 border-t border-[rgba(11,12,13,0.08)] pt-4 text-left" data-testid="bake-summary-items">
          {ladderItems.map((it) => (
            <div key={it.product_id} className="flex items-baseline justify-between gap-3 py-1.5" data-testid={`bake-summary-item-${it.product_id}`}>
              <p className="font-body text-[var(--hoa-ink-950)] text-sm min-w-0">
                <span className="text-[rgba(11,12,13,0.55)]">{it.category} · </span>
                {it.name} <span className="text-[rgba(11,12,13,0.5)]">× {it.qty}</span>
              </p>
              <p className="font-body text-[rgba(11,12,13,0.7)] text-sm shrink-0">{inr(it.cost)}</p>
            </div>
          ))}
        </div>
        <PricingLadder totalCost={result.total_cost} items={result.items} extraPct={extraPct} setExtraPct={setExtraPct} />
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <button
          data-testid="bake-confirm-order"
          onClick={onConfirm}
          className="w-full inline-flex items-center justify-center gap-2 font-label text-[12px] tracking-[0.2em] uppercase h-14 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] shadow-[0_12px_36px_rgba(0,0,0,0.35)] hover:brightness-110 transition-[filter]"
        >
          <CheckCircle2 className="w-5 h-5" strokeWidth={1.8} /> Confirm Order
        </button>
        <button
          data-testid="bake-edit-order"
          onClick={onEdit}
          className="w-full inline-flex items-center justify-center gap-2 font-label text-[12px] tracking-[0.18em] uppercase h-14 rounded-full border border-[rgba(179,145,74,0.45)] text-[var(--hoa-cream-50)] hover:bg-[rgba(240,233,216,0.06)] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.6} /> Edit this order
        </button>
        <button
          data-testid="bake-summary-new-order"
          onClick={onNew}
          className="w-full inline-flex items-center justify-center gap-2 font-label text-[12px] tracking-[0.18em] uppercase h-14 rounded-full border border-[rgba(179,145,74,0.3)] text-[rgba(240,233,216,0.7)] hover:bg-[rgba(240,233,216,0.05)] hover:text-[var(--hoa-cream-50)] transition-colors"
        >
          <RotateCcw className="w-5 h-5" strokeWidth={1.6} /> Create new order
        </button>
      </div>
    </div>
  );
}

function IngredientSections({ ingredients }) {
  const sections = useMemo(() => {
    const map = new Map();
    ingredients.forEach((ing) => {
      if (!map.has(ing.section)) map.set(ing.section, []);
      map.get(ing.section).push(ing);
    });
    return [...map.entries()];
  }, [ingredients]);

  return sections.map(([section, ings]) => (
    <div key={section} className="mt-5">
      <div className="flex items-center gap-3">
        <span className="font-label text-[9px] tracking-[0.26em] uppercase hoa-gold-text">{section}</span>
        <span className="flex-1 h-px bg-[rgba(179,145,74,0.2)]" />
      </div>
      <div className="mt-2 border border-[rgba(179,145,74,0.3)] rounded-[var(--hoa-radius-md)] bg-[rgba(240,233,216,0.03)] divide-y divide-[rgba(179,145,74,0.12)]">
        {ings.map((ing) => (
          <div key={ing.name} className="flex items-center gap-3 px-4 py-2.5">
            <p className="font-body text-[var(--hoa-cream-50)] text-sm flex-1 min-w-0">{ing.name}</p>
            <p className="font-body text-[var(--hoa-gold-300)] text-sm shrink-0">{ing.amount}</p>
            <p className="font-label text-[9px] tracking-[0.1em] text-[rgba(240,233,216,0.45)] w-14 text-right shrink-0">
              {ing.cost > 0 ? inr(ing.cost) : "—"}
            </p>
          </div>
        ))}
      </div>
    </div>
  ));
}

const EXPORT_MAX_ROWS = 18;

function groupBySection(ingredients) {
  const order = [];
  const map = new Map();
  ingredients.forEach((ing) => {
    if (!map.has(ing.section)) {
      map.set(ing.section, []);
      order.push(ing.section);
    }
    map.get(ing.section).push(ing);
  });
  return order.map((s) => [s, map.get(s)]);
}

function paginateExport(result) {
  const units = [];
  const push = (unit, cost, opts = {}) => units.push({ unit, cost, ...opts });
  const pushSections = (ingredients, stickFirst) => {
    let stick = stickFirst;
    groupBySection(ingredients).forEach(([name, rows]) => {
      for (let i = 0; i < rows.length; i += EXPORT_MAX_ROWS - 3) {
        const chunk = rows.slice(i, i + EXPORT_MAX_ROWS - 3);
        push({ type: "section", name: i === 0 ? name : `${name} (contd.)`, rows: chunk }, chunk.length + 1, { stick });
        stick = false;
      }
    });
  };

  push({ type: "order", items: result.items }, result.items.length + 1);
  push({ type: "heading", text: "All together" }, 2, { pageBreak: true });
  pushSections(result.ingredients, true);
  push({ type: "heading", text: "Item by item" }, 2, { pageBreak: true });
  result.items.forEach((it, i) => {
    push({ type: "product", name: `${it.category} · ${it.name} × ${it.qty}` }, 2, i === 0 ? { stick: true } : { pageBreak: true });
    pushSections(it.ingredients, true);
  });

  const groups = [];
  units.forEach((u) => {
    if (u.stick && groups.length) groups[groups.length - 1].push(u);
    else groups.push([u]);
  });

  const pages = [];
  let cur = { units: [], rows: 0 };
  const newPage = () => {
    if (cur.units.length) pages.push(cur);
    cur = { units: [], rows: 0 };
  };
  groups.forEach((g) => {
    const cost = g.reduce((a, u) => a + u.cost, 0);
    if (g[0].pageBreak || (cur.rows + cost > EXPORT_MAX_ROWS && cur.rows > 0)) newPage();
    g.forEach((u) => {
      cur.units.push(u.unit);
      cur.rows += u.cost;
    });
  });
  newPage();
  return pages;
}

function ExportUnit({ unit }) {
  if (unit.type === "order") {
    return unit.items.map((it) => (
      <p key={it.product_id} className="font-body" style={{ fontSize: 32, color: "#16352a", padding: "4px 0" }}>
        {it.name} × {it.qty} <span style={{ color: "rgba(22,53,42,0.55)" }}>({it.category})</span>
      </p>
    ));
  }
  if (unit.type === "heading") {
    return (
      <p className="font-display" style={{ fontSize: 45, color: "#16352a", marginTop: 33 }}>{unit.text}</p>
    );
  }
  if (unit.type === "product") {
    return (
      <p
        className="font-body"
        style={{ fontSize: 34, fontWeight: 600, color: "#16352a", borderBottom: "2px solid rgba(138,109,51,0.5)", paddingBottom: 6, marginTop: 27 }}
      >
        {unit.name}
      </p>
    );
  }
  return (
    <div style={{ marginTop: 20 }}>
      <p className="font-label uppercase" style={{ fontSize: 20, letterSpacing: "0.24em", color: "#8A6D33" }}>
        {unit.name}
      </p>
      {unit.rows.map((ing) => (
        <div
          key={ing.name}
          style={{ display: "flex", justifyContent: "space-between", gap: 18, padding: "10px 0", borderBottom: "1px solid rgba(22,53,42,0.18)" }}
        >
          <span className="font-body" style={{ fontSize: 32, color: "#16352a" }}>{ing.name}</span>
          <span className="font-body" style={{ fontSize: 32, color: "#6B5426", whiteSpace: "nowrap" }}>{ing.amount}</span>
        </div>
      ))}
    </div>
  );
}

function ExportLogo() {
  return (
    <div style={{ position: "relative", width: 72, marginBottom: 14 }}>
      <svg viewBox="0 0 225.12 225.54" style={{ display: "block", width: "100%", height: "auto" }} aria-hidden="true">
        <path d={A_PATH} fill="#16352a" fillRule="evenodd" />
      </svg>
      <div
        style={{
          position: "absolute",
          left: "98%",
          top: "-8%",
          width: "40%",
          height: "40%",
          border: "5px solid #B3914A",
          borderRadius: "50%",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

function ExportPricing({ result, extraPct }) {
  const { ladderCost, subtotal, pct, surpluses, flats, mrp } = computePricing(result.total_cost, result.items, extraPct);
  const row = { display: "flex", justifyContent: "space-between", gap: 18, padding: "6px 0" };
  return (
    <div style={{ marginTop: 30 }}>
      <p className="font-label uppercase" style={{ fontSize: 20, letterSpacing: "0.24em", color: "#8A6D33" }}>
        Pricing
      </p>
      <div style={{ ...row, marginTop: 6 }}>
        <span className="font-body" style={{ fontSize: 26, color: "#16352a" }}>Making cost</span>
        <span className="font-body" style={{ fontSize: 26, color: "#16352a", whiteSpace: "nowrap" }}>{inr(ladderCost)}</span>
      </div>
      {COST_COMPONENTS.map((c) => (
        <div key={c.slug} style={row}>
          <span className="font-body" style={{ fontSize: 26, color: "rgba(22,53,42,0.65)" }}>{c.label}</span>
          <span className="font-body" style={{ fontSize: 26, color: "#6B5426", whiteSpace: "nowrap" }}>+ {inr(ladderCost * c.pct)}</span>
        </div>
      ))}
      <div style={{ ...row, borderTop: "1px solid rgba(22,53,42,0.18)" }}>
        <span className="font-body" style={{ fontSize: 26, fontWeight: 600, color: "#16352a" }}>Subtotal</span>
        <span className="font-body" style={{ fontSize: 26, fontWeight: 600, color: "#16352a", whiteSpace: "nowrap" }}>{inr(subtotal)}</span>
      </div>
      {pct > 0 && (
        <div style={row}>
          <span className="font-body" style={{ fontSize: 26, color: "rgba(22,53,42,0.65)" }}>Profit {pct}%</span>
          <span className="font-body" style={{ fontSize: 26, color: "#6B5426", whiteSpace: "nowrap" }}>+ {inr(subtotal * (pct / 100))}</span>
        </div>
      )}
      {surpluses.map((s) => (
        <div key={s.id} style={row}>
          <span className="font-body" style={{ fontSize: 26, color: "rgba(22,53,42,0.65)" }}>{s.name} surplus {s.pct}%</span>
          <span className="font-body" style={{ fontSize: 26, color: "#6B5426", whiteSpace: "nowrap" }}>+ {inr(s.amount)}</span>
        </div>
      ))}
      {flats.map((f) => (
        <div key={f.id} style={row}>
          <span className="font-body" style={{ fontSize: 26, color: "rgba(22,53,42,0.65)" }}>
            {f.category} · {f.name} · {inr(f.unit)} × {f.qty}
          </span>
          <span className="font-body" style={{ fontSize: 26, color: "#6B5426", whiteSpace: "nowrap" }}>+ {inr(f.amount)}</span>
        </div>
      ))}
      <div style={{ ...row, borderTop: "2px solid rgba(138,109,51,0.5)", marginTop: 6, paddingTop: 12, alignItems: "baseline" }}>
        <span className="font-body" style={{ fontSize: 28, fontWeight: 700, color: "#16352a" }}>Total (rounded)</span>
        <span className="font-display" style={{ fontSize: 46, color: "#16352a", whiteSpace: "nowrap" }}>{inr(mrp)}</span>
      </div>
    </div>
  );
}

function ExportPages({ result, pagesRef, extraPct }) {
  const pages = useMemo(() => paginateExport(result), [result]);
  const date = new Date(result.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  pagesRef.current = pagesRef.current.slice(0, pages.length);
  return pages.map((page, idx) => (
    <div
      key={idx}
      ref={(el) => {
        pagesRef.current[idx] = el;
      }}
      data-testid={`bake-export-page-${idx + 1}`}
      style={{ position: "fixed", left: "-10000px", top: 0, width: 720, backgroundColor: "#F1E9D6", padding: "40px 46px" }}
    >
      {idx === 0 && (
        <>
          <ExportLogo />
          <p className="font-display" style={{ fontSize: 48, color: "#16352a" }}>house of appetite</p>
          {(result.customer_name || result.customer_phone) && (
            <p className="font-body" style={{ fontSize: 33, fontWeight: 700, color: "#16352a", marginTop: 10 }}>
              {[result.customer_name, result.customer_phone].filter(Boolean).join(" · ")}
            </p>
          )}
        </>
      )}
      <p className="font-label uppercase" style={{ fontSize: 20, letterSpacing: "0.28em", color: "#8A6D33", marginTop: idx === 0 ? 10 : 0 }}>
        Ingredients · {date}
        {pages.length > 1 ? ` · Page ${idx + 1}/${pages.length}` : ""}
      </p>
      <div style={{ height: 2, backgroundColor: "rgba(138,109,51,0.4)", margin: "21px 0" }} />
      {page.units.map((unit, i) => (
        <ExportUnit key={i} unit={unit} />
      ))}
      {idx === 0 && <ExportPricing result={result} extraPct={extraPct} />}
      <p className="font-label uppercase" style={{ fontSize: 16, letterSpacing: "0.26em", color: "rgba(22,53,42,0.5)", marginTop: 36, textAlign: "center" }}>
        baked with warmth of love · give in.
      </p>
    </div>
  ));
}

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

const slug = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export function IngredientsView({ result, onBack, onNew, extraPct }) {
  const [split, setSplit] = useState("combined");
  const [busy, setBusy] = useState("");
  const pagesRef = useRef([]);

  const pdfName = `hoa-ingredients${result.customer_name ? `-${slug(result.customer_name)}` : ""}-${new Date().toISOString().slice(0, 10)}.pdf`;
  const shareText = [
    "House of Appetite — Ingredients",
    result.customer_name ? `Customer: ${result.customer_name}` : "",
    result.customer_phone ? `Phone: ${result.customer_phone}` : "",
  ].filter(Boolean).join("\n");

  const makePdf = async () => {
    const html2canvas = (await import("html2canvas")).default;
    const { jsPDF } = await import("jspdf");
    const nodes = pagesRef.current.filter(Boolean);
    let pdf = null;
    for (const node of nodes) {
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#F1E9D6" });
      const w = canvas.width / 2;
      const h = canvas.height / 2;
      if (!pdf) pdf = new jsPDF({ unit: "px", format: [w, h], orientation: w > h ? "l" : "p", hotfixes: ["px_scaling"] });
      else pdf.addPage([w, h], w > h ? "l" : "p");
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, w, h);
    }
    return new File([pdf.output("blob")], pdfName, { type: "application/pdf" });
  };

  const shareWhatsApp = async () => {
    setBusy("share");
    try {
      const file = await makePdf();
      const payload = { files: [file], text: shareText, title: "House of Appetite — Ingredients" };
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share(payload);
      } else {
        downloadBlob(file, pdfName);
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener");
      }
    } catch (e) {
      /* user cancelled share sheet */
    } finally {
      setBusy("");
    }
  };

  const savePdf = async () => {
    setBusy("save");
    try {
      const file = await makePdf();
      downloadBlob(file, pdfName);
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="bake-result" className="mt-8">
      <div className="flex items-center gap-3">
        <ChefHat className="w-4 h-4 text-[var(--hoa-gold-400)]" strokeWidth={1.6} />
        <h3 className="font-display text-[var(--hoa-cream-50)] text-xl sm:text-2xl">Ingredients needed</h3>
      </div>

      <div className="flex gap-2 mt-4" data-testid="bake-split-toggle">
        {[
          { key: "combined", label: "All together", Icon: Layers },
          { key: "byitem", label: "Item by item", Icon: ListOrdered },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            data-testid={`bake-split-${key}`}
            onClick={() => setSplit(key)}
            className={`inline-flex items-center gap-1.5 h-10 px-5 rounded-full border font-label text-[9px] sm:text-[10px] tracking-[0.16em] uppercase transition-colors ${
              split === key
                ? "border-[var(--hoa-gold-500)] bg-[rgba(179,145,74,0.16)] text-[var(--hoa-cream-50)]"
                : "border-[rgba(179,145,74,0.3)] text-[rgba(240,233,216,0.65)] hover:bg-[rgba(240,233,216,0.05)]"
            }`}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {label}
          </button>
        ))}
      </div>

      <div data-testid="bake-ingredients-list">
        {split === "combined" ? (
          <IngredientSections ingredients={result.ingredients} />
        ) : (
          result.items.map((it) => (
            <div key={it.product_id} className="mt-8" data-testid={`bake-item-ingredients-${it.product_id}`}>
              <div className="flex items-baseline justify-between gap-3 border-b border-[rgba(179,145,74,0.35)] pb-2">
                <h4 className="font-display text-[var(--hoa-cream-50)] text-lg sm:text-xl">
                  <span className="text-[rgba(240,233,216,0.6)]">{it.category} · </span>
                  {it.name} <span className="text-[var(--hoa-gold-300)]">× {it.qty}</span>
                </h4>
              </div>
              <IngredientSections ingredients={it.ingredients} />
            </div>
          ))
        )}
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <button
          data-testid="bake-share-whatsapp"
          onClick={shareWhatsApp}
          disabled={busy !== ""}
          className="w-full inline-flex items-center justify-center gap-2 font-label text-[12px] tracking-[0.2em] uppercase h-14 rounded-full bg-[#25D366] text-[#0B3D22] shadow-[0_12px_36px_rgba(0,0,0,0.35)] hover:brightness-105 transition-[filter] disabled:opacity-60"
        >
          {busy === "share" ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" strokeWidth={1.8} />}
          Share on WhatsApp
        </button>
        <button
          data-testid="bake-save-pdf"
          onClick={savePdf}
          disabled={busy !== ""}
          className="w-full inline-flex items-center justify-center gap-2 font-label text-[12px] tracking-[0.18em] uppercase h-14 rounded-full border border-[rgba(179,145,74,0.45)] text-[var(--hoa-cream-50)] hover:bg-[rgba(240,233,216,0.06)] transition-colors disabled:opacity-60"
        >
          {busy === "save" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" strokeWidth={1.6} />}
          Save as PDF
        </button>
        <button
          data-testid="bake-back-to-summary"
          onClick={onBack}
          className="w-full inline-flex items-center justify-center gap-2 font-label text-[12px] tracking-[0.18em] uppercase h-14 rounded-full border border-[rgba(179,145,74,0.45)] text-[var(--hoa-cream-50)] hover:bg-[rgba(240,233,216,0.06)] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.6} /> Back to summary
        </button>
        <button
          data-testid="bake-new-order"
          onClick={onNew}
          className="w-full inline-flex items-center justify-center gap-2 font-label text-[12px] tracking-[0.2em] uppercase h-14 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] shadow-[0_12px_36px_rgba(0,0,0,0.35)] hover:brightness-110 transition-[filter]"
        >
          <RotateCcw className="w-5 h-5" strokeWidth={1.8} /> New order
        </button>
      </div>

      <ExportPages result={result} pagesRef={pagesRef} extraPct={extraPct} />
    </div>
  );
}

export const BakeOrderBuilder = ({ onAuthError }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("All");
  const [quantities, setQuantities] = useState({});
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [extraPct, setExtraPct] = useState("100");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState("build");
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/admin/recipes/products")
      .then((res) => setCategories(res.data || []))
      .catch((e) => onAuthError && onAuthError(e))
      .finally(() => setLoading(false));
  }, [onAuthError]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stage]);

  const totalUnits = useMemo(() => Object.values(quantities).reduce((a, b) => a + b, 0), [quantities]);

  const setQty = (id, qty) => {
    setQuantities((q) => {
      const next = { ...q };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });
    setError("");
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const items = Object.entries(quantities).map(([product_id, qty]) => ({ product_id, qty }));
      const res = await api.post("/admin/bake-orders", { customer_name: customerName, customer_phone: customerPhone, extra_pct: Math.max(0, parseFloat(extraPct) || 0), items });
      setResult(res.data);
      setStage("summary");
    } catch (e) {
      if (e?.response?.status === 401 && onAuthError) onAuthError(e);
      else setError("Could not calculate the order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="bake-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--hoa-gold-400)]" />
      </div>
    );
  }

  const resetAll = () => {
    setResult(null);
    setQuantities({});
    setCustomerName("");
    setCustomerPhone("");
    setExtraPct("100");
    setStage("build");
  };

  if (stage === "summary" && result) {
    return (
      <SummaryView
        result={result}
        extraPct={extraPct}
        setExtraPct={setExtraPct}
        onConfirm={() => setStage("ingredients")}
        onEdit={() => setStage("build")}
        onNew={resetAll}
      />
    );
  }

  if (stage === "ingredients" && result) {
    return <IngredientsView result={result} extraPct={extraPct} onBack={() => setStage("summary")} onNew={resetAll} />;
  }

  const visibleCats = activeCat === "All" ? categories : categories.filter((c) => c.category === activeCat);

  return (
    <div data-testid="bake-builder" className="mt-8 pb-24">
      <input
        data-testid="bake-customer-name-input"
        className="w-full h-12 px-4 rounded-[12px] bg-[rgba(240,233,216,0.05)] border border-[rgba(179,145,74,0.35)] font-body text-sm text-[var(--hoa-cream-50)] placeholder:text-[rgba(240,233,216,0.35)] focus:outline-none focus:border-[var(--hoa-gold-400)] transition-colors"
        placeholder="Customer name (optional)"
        value={customerName}
        onChange={(e) => setCustomerName(e.target.value)}
        maxLength={80}
      />

      <input
        data-testid="bake-customer-phone-input"
        type="tel"
        inputMode="tel"
        className="mt-3 w-full h-12 px-4 rounded-[12px] bg-[rgba(240,233,216,0.05)] border border-[rgba(179,145,74,0.35)] font-body text-sm text-[var(--hoa-cream-50)] placeholder:text-[rgba(240,233,216,0.35)] focus:outline-none focus:border-[var(--hoa-gold-400)] transition-colors"
        placeholder="Customer phone number (optional)"
        value={customerPhone}
        onChange={(e) => setCustomerPhone(e.target.value.replace(/[^\d+\s-]/g, "").slice(0, 20))}
        maxLength={20}
      />

      <div className="flex gap-2 overflow-x-auto pt-4 pb-2 -mx-5 px-5 sm:mx-0 sm:px-0" data-testid="bake-category-chips">
        {["All", ...categories.map((c) => c.category)].map((cat) => (
          <button
            key={cat}
            type="button"
            data-testid={`bake-category-chip-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setActiveCat(cat)}
            className={`shrink-0 h-9 px-4 rounded-full border font-label text-[9px] sm:text-[10px] tracking-[0.16em] uppercase transition-colors ${
              activeCat === cat
                ? "border-[var(--hoa-gold-500)] bg-[rgba(179,145,74,0.16)] text-[var(--hoa-cream-50)]"
                : "border-[rgba(179,145,74,0.3)] text-[rgba(240,233,216,0.65)] hover:bg-[rgba(240,233,216,0.05)]"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {visibleCats.map((cat) => (
        <div key={cat.category} className="mt-6">
          <div className="flex items-center gap-3">
            <span className="font-label text-[9px] tracking-[0.26em] uppercase hoa-gold-text">{cat.category}</span>
            <span className="flex-1 h-px bg-[rgba(179,145,74,0.2)]" />
          </div>
          <div className="mt-1">
            {cat.products.map((p) => (
              <ProductRow key={p.id} product={p} qty={quantities[p.id] || 0} onChange={setQty} />
            ))}
          </div>
        </div>
      ))}

      {error && (
        <p className="font-body text-sm text-[#c96f5a] mt-4" data-testid="bake-error">
          {error}
        </p>
      )}

      {totalUnits > 0 && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-auto sm:sticky sm:bottom-6 z-40 mx-auto max-w-[560px]">
          <button
            data-testid="bake-submit-button"
            onClick={submit}
            disabled={submitting}
            className="w-full h-14 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] font-label text-[11px] tracking-[0.2em] uppercase shadow-[0_12px_36px_rgba(0,0,0,0.45)] hover:brightness-110 transition-[filter] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Sparkles className="w-4 h-4" strokeWidth={1.8} />
                Let's Bake · {totalUnits} {totalUnits === 1 ? "item" : "items"}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
